<template>
  <section
    v-bind="sectionTitleAttrs"
    data-test="chats-list-item"
    @contextmenu.prevent="showContext($event)"
    @mouseenter.stop="onTooltipMouseEnter"
    @mouseover.stop="onTooltipMouseOver"
    @mouseleave.stop="onTooltipMouseLeave"
  >
    <div
      class="flex h-18 items-center rounded-8 px-3 gap-3 cursor-pointer hover:bg-bg-base-bg3 group transition-all"
      :class="{
        'bg-bg-base-bg4': isSelected,
      }"
      @click.right.shift.stop
    >
      <div
        ref="el"
        class="relative flex-none"
      >
        <div class="relative select-none">
          <contact-avatar
            :is-room="room.isGroup"
            :is-own="isMyOwn"
            :room="room"
            :contact="contact"
            :name="contact ? undefined : contactName"
            :load-image="loadAvatarImage"
          />
          <div
            v-if="showRoomEventThumbnail"
            data-test="last-message-contact-thumbnail"
            class="absolute -bottom-1 -right-1 rounded-full ring-1 ring-bg-base-bg1"
            @mouseenter.stop="onThumbnailTooltipMouseEnter"
            @mouseover.stop="onThumbnailTooltipMouseOver"
            @mouseleave.stop="onThumbnailTooltipMouseLeave"
          >
            <contact-avatar
              :contact="lastMessageContact"
              :size="24"
              :load-image="loadAvatarImage"
            />
          </div>
        </div>

        <template v-if="contact && !room.isGroup && !isMyOwn && !isBot">
          <contact-status
            :p-served="contact.imLogin"
            compact-mode
          />
        </template>
      </div>

      <div class="grid grow min-w-0 grid-rows-[auto_auto] gap-y-1">
        <div class="grid grid-cols-[1fr_auto] items-center">
          <div class="min-w-0 overflow-hidden">
            <div class="flex items-center gap-1.5">
              <wui-icon
                v-if="room.mode === ConferenceType.MODERATED"
                class="shrink-0 text-surf-brand-basedarkblue-def"
                small
                name="moderatorM"
              />
              <span class="truncate text-fg-base-fg3 text-body-m-15-22">
                {{ contactName }}
              </span>
              <wui-icon
                v-if="areNotificationsDisabled"
                name="bellOffM"
                class="shrink-0 text-fg-base-fg22"
                small
                :title="$t('common.notificationsAreDisabled')"
              />
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <div class="shrink-0 group-hover:hidden">
              <message-status
                v-if="isMessageReadStatusVisible"
                :is-group="!!room?.isGroup"
                :event="lastMessage as HistoryMessageItem<MessageOutgoingEvent>"
                :contact-id="room.id"
                :state="isSelected ? 'item-selected' : 'default'"
              />
            </div>
            <p
              v-if="!roomStatus?.active"
              class="text-label-r-12-18 text-fg-base-fg17 group-hover:hidden"
            >
              {{ date }}
            </p>
            <wui-btn
              v-if="!roomStatus?.active"
              variant="neut"
              state="alpha"
              icon
              :size="24"
              prepend-icon="kebabHorizontalM"
              class="hidden! h-5! w-5! min-h-5! min-w-5! shrink-0 p-0! group-hover:inline-flex! hover:bg-transparent! [&_svg]:h-5! [&_svg]:w-5!"
              data-test="chat-list-item-kebab"
              :aria-label="$t('common.more')"
              @click.stop.prevent="showContext($event)"
            />
          </div>
        </div>
        <div class="grid h-5 grid-cols-[1fr_auto] items-center gap-x-1 text-body-r-14-20">
          <div
            v-if="duration && startedTimestamp && roomStatus?.active"
            data-test="duration"
            class="text-body-r-14-20 text-surf-brand-basedarkblue-def"
          >
            {{ duration }}
          </div>
          <typing-label
            v-else-if="currentUser?.imLogin !== room.pServedUser"
            class="grid min-w-0 text-surf-brand-basedarkblue-def"
            :p-served="room.pServedUser"
            :is-group="room.isGroup"
            :with-names="room.isGroup"
            :max-named-typers="2"
          >
            <component
              :is="preview"
              class="col-span-1 text-fg-base-fg17 truncate"
              :draft="room.draft"
              :event="lastMessage"
              :p-served="room.pServedUser"
              :name="emptySearchPreviewName"
            />
          </typing-label>
          <component
            :is="preview"
            v-else
            class="col-span-1 min-w-0 text-fg-base-fg17 truncate"
            :event="room.lastMessage"
            :draft="room.draft"
            :p-served="room.pServedUser"
            :name="emptySearchPreviewName"
          />
          <span class="flex shrink-0 gap-x-1 items-center">
            <wui-icon
              v-if="hasReactionOnLastOutgoingMessage"
              name="heartM"
              class="shrink-0 text-surf-neg-base-def"
              small
              data-test="chat-list-reaction-icon"
            />
            <wui-icon
              v-if="hasUnreadMention && room.isGroup"
              name="atM"
              class="shrink-0 text-surf-brand-base-def"
              small
              data-test="chat-list-mention-icon"
            />
            <app-badge
              v-if="unreadEventsCount"
              :value="unreadEventsCount"
              :disabled="areNotificationsDisabled && noImportantMessages"
            />
            <wui-icon
              v-else-if="showPin && room.isPinned && !roomStatus?.active"
              class="shrink-0 text-fg-base-fg14"
              name="pinM"
              small
            />
          </span>
        </div>
      </div>
      <div
        v-if="roomStatus?.active || session && !isSessionRingingEnabled(session.sessionId) || pickupNotification?.eventData"
        class="flex gap-x-2 items-center"
      >
        <wui-btn
          v-if="session && !isSessionRingingEnabled(session.sessionId)"
          class="shrink-0! animate-border-pulse-green-small"
          :title="$t('mainAside.showCallWindow')"
          variant="positive"
          state="filled"
          icon
          :prepend-icon="session.direction === 'outgoing' ? 'phoneOutM' : 'phoneIncM'"
          @click="session?.dialog.showDialog()"
        />
        <wui-btn
          v-else-if="roomStatus?.active"
          class="shrink-0! animate-border-pulse-green-small"
          :class="{'border-primary-tints-300' : isSelected}"
          :title="$t('mainAside.joinToConversation')"
          variant="positive"
          state="filled"
          icon
          prepend-icon="phoneF"
          @click.stop="prepareToCall()"
        />
        <answer-pickup-call-button
          v-else-if="pickupNotification?.eventData"
          :event="pickupNotification.eventData"
          :type="pickupNotification.type"
        >
          <template #default="{ pickupCall, title }">
            <wui-btn
              data-test="btn"
              class="shrink-0! text-spanish-orange! opacity-100! border border-primary-tints-150 hover:border-spanish-orange active:text-white! active:bg-spanish-orange! animate-border-pulse-orange"
              icon
              prepend-icon="callInterception"
              :title="title"
              @click="pickupCall()"
            />
          </template>
        </answer-pickup-call-button>
      </div>
    </div>
    <contact-hover-tooltip
      v-if="isOneToOneChat && tooltipContact"
      :contact="tooltipContact"
      :visible="isTooltipVisible"
      :mouse-x="mouseX"
      :mouse-y="mouseY"
    />
    <contact-hover-tooltip
      v-if="showRoomEventThumbnail && lastMessageTooltipContact"
      :contact="lastMessageTooltipContact"
      :visible="isThumbnailTooltipVisible"
      :mouse-x="thumbnailMouseX"
      :mouse-y="thumbnailMouseY"
    />
  </section>
</template>

<script lang="ts" setup>
import { WuiIcon, WuiBtn } from '@wui/common-library'
import {
  isConferenceEditMessage,
  HistoryMessageItem,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  ConferenceChangedByYouEvent,
  ConferenceChangedByOtherEvent,
  isBotPServed,
  isMessageDeleted,
  ConferenceType,
  MessageStatusType,
} from '@wui/im'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRef,
  watch,
} from 'vue'
import { useRouter } from 'vue-router'


import { AnswerPickupCallButton } from '@/features/answer-pickup-call'

import { useCallSessionRoomStore, useCallSessionStartedTimestampStore, getConferenceTimerOffsetSeconds } from '@/entities/call-session'
import { useCallRingingStore } from '@/entities/calls'
import { useRoomPreview } from '@/entities/chat-last-message-preview'
import { useStoreContacts, ContactHoverTooltip, useContactHoverTooltip, type ContactTooltipData } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { chatModel, useChatMessageStore, MessageStatus } from '@/entities/messages'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreRooms, useNotificationPreferenceStore, useIntercomRoomParticipantsStatusesStore } from '@/entities/room'
import { TypingLabel } from '@/entities/typing'
import { RTCSessionFacade, useWebRTC, useSessionTimer } from '@/entities/webrtc'

import { AppBadge } from '@/shared/components/app-badge'
import { notifications } from '@/shared/composables/calls-notifications'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { ContactStatus } from '@/shared/contact-status'
import { Contact } from '@/shared/types/api'
import { RoomListItem } from '@/shared/types/app'
import { PREPARE_TO_CALL } from '@/shared/utils/constants'
import { convertTimestampToDateTimeString } from '@/shared/utils/date-time'

const props = defineProps({
  room: {
    type: Object as PropType<RoomListItem>,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
  showPin: {
    type: Boolean,
    default: true,
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits<{
  (event: 'show-context', e: MouseEvent | TouchEvent, room: RoomListItem): void
}>()

/** Sticky: once row was visible, keep avatar URL so recycle doesn't flash initials. Reset on room change. */
const loadAvatarImage = ref(props.isVisible)
watch(() => props.isVisible, (visible) => {
  if (visible) loadAvatarImage.value = true
})
watch(() => props.room.roomId, () => {
  loadAvatarImage.value = props.isVisible
})

const showContext = (event: MouseEvent | TouchEvent) => {
  emits('show-context', event, props.room)
}

const { isSessionRingingEnabled } = useCallRingingStore()
const { currentUser } = useCurrentUser()
const storeRooms = useStoreRooms()
const { getRoomStatus } = useIntercomRoomParticipantsStatusesStore()
const { t } = useLocalization()
const { sessions } = useWebRTC()
const { getRoom: getSessionRoom } = useCallSessionRoomStore()
const { getAccessByRoomId } = useNotificationPreferenceStore()
const { findByPServed } = useStoreContacts()
const { isCurrentUserMentionedOrQuotedAtUnreadMessages } = useChatMessageStore()
const { startedCallsTimestamps } = storeToRefs(useCallSessionStartedTimestampStore())
const { reactionEvents } = storeToRefs(useReactionsStore())

const noImportantMessages = computed(() => {
  return !isCurrentUserMentionedOrQuotedAtUnreadMessages(props.room.pServedUser)
})

const hasUnreadMention = computed(() => {
  return isCurrentUserMentionedOrQuotedAtUnreadMessages(props.room.pServedUser)
})

const room = toRef(props, 'room')

const preview = useRoomPreview(room)

const isOneToOneChat = computed(() => !room.value.isGroup && !isMyOwn.value)

const {
  mouseX,
  mouseY,
  isTooltipVisible,
  onMouseEnter: onTooltipMouseEnter,
  onMouseOver: onTooltipMouseOver,
  onMouseLeave: onTooltipMouseLeave,
} = useContactHoverTooltip()

const {
  mouseX: thumbnailMouseX,
  mouseY: thumbnailMouseY,
  isTooltipVisible: isThumbnailTooltipVisible,
  onMouseEnter: onThumbnailTooltipMouseEnter,
  onMouseOver: onThumbnailTooltipMouseOver,
  onMouseLeave: onThumbnailTooltipMouseLeave,
} = useContactHoverTooltip()

const lastMessage = computed(() => {
  return room.value.lastMessage
})

const isLastMessageOutgoing = computed(() => {
  return room.value.lastMessage?.item.type === 'OutgoingMessageEvent'
})

const hasReactionOnLastOutgoingMessage = computed(() => {
  if (!isLastMessageOutgoing.value || !lastMessage.value?.eventId) {
    return false
  }

  const reactions = reactionEvents.value.get(lastMessage.value.eventId)
  return Boolean(reactions?.size)
})

const contact = computed<Contact | undefined>(
  () => findByPServed(props.room.pServedUser),
)

const tooltipContact = computed<ContactTooltipData | null>(() => {
  if (!isOneToOneChat.value) {
    return null
  }

  if (contact.value) {
    return {
      name: contact.value.name,
      internalNumber: contact.value.internalNumber,
      ...(contact.value.mobilePhone ? { mobilePhone: contact.value.mobilePhone } : {}),
    }
  }

  const { name, number } = room.value
  if (!name && !number) {
    return null
  }

  return {
    name,
    internalNumber: number,
  }
})

const areNotificationsDisabled = computed(() => {
  return getAccessByRoomId(room.value.pServedUser)
})

const isMyOwn = computed(() => room.value.pServedUser === currentUser.value?.imLogin)
const isBot = computed(() => isBotPServed(room.value.pServedUser))

const session = computed<RTCSessionFacade | undefined>(() => {
  return Array.from(sessions.value.values()).find((s) => {
    return getSessionRoom(s.sessionId)?.pServedUser === room.value.pServedUser
  })
})

const el = ref<HTMLElement>()

const contactName = computed(() => {
  return room.value?.name || t('common.unknownUser')
})

const emptySearchPreviewName = computed(() => {
  if (!props.isSearching || lastMessage.value || room.value.draft) {
    return undefined
  }

  return contactName.value
})

const sectionTitleAttrs = computed(() => (
  isOneToOneChat.value ? {} : { title: contactName.value }
))

const showRoomEventThumbnail = computed(() => {
  if (!room.value.isGroup) return false
  if (!room.value.lastMessage) return false
  if (!lastMessageContact.value) return false
  return true
})

const lastMessageContact = computed(() => {
  if (!room.value.lastMessage) {
    return undefined
  }
  let isCurrentUser = false
  if (isConferenceEditMessage(room.value.lastMessage)) {
    const item = room.value.lastMessage.item as (ConferenceChangedByYouEvent | ConferenceChangedByOtherEvent)
    isCurrentUser = item.executor === currentUser.value?.imLogin
    if (!isCurrentUser) {
      return findByPServed(item.executor)
    }
  }
  if (room.value.lastMessage.item.type === 'OutgoingMessageEvent' || isCurrentUser) {
    return currentUser.value
  }

  const executor = (room.value.lastMessage as HistoryMessageItem<MessageIncomingEvent>).item.executor

  if (executor === currentUser.value?.imLogin) {
    return currentUser.value
  }

  const contact = findByPServed(executor)
  return contact
})

const lastMessageTooltipContact = computed<ContactTooltipData | null>(() => {
  const messageContact = lastMessageContact.value
  if (!messageContact) {
    return null
  }

  return {
    name: messageContact.name,
    internalNumber: messageContact.internalNumber,
    ...(messageContact.mobilePhone ? { mobilePhone: messageContact.mobilePhone } : {}),
  }
})

const { getUnreadCount } = chatModel.useChatMessageStore()

const unreadEventsCount = computed(() => {
  const room = storeRooms.getRoomById(props.room.roomId)
  return room ? getUnreadCount(room?.pServedUser) : 0
})

const date = computed(() => {
  return convertTimestampToDateTimeString(room.value?.lastMessage?.timestamp)
})

const roomStatus = computed(() => getRoomStatus(props.room.pServedUser))

const router = useRouter()

const prepareToCall = async () => {
  await router.push({ name: PREPARE_TO_CALL, params: { roomId: props.room.id }})
}

const isMessageReadStatusVisible = computed(() => {
  const lastMsg = lastMessage.value as HistoryMessageItem<MessageOutgoingEvent>
  if (!lastMsg) {
    return false
  }

  if (lastMsg.status === MessageStatusType.INITIAL || lastMsg.status === MessageStatusType.ERROR) {
    return false
  }

  return [
    lastMsg.item.executor !== lastMsg.item.pServed,
    isLastMessageOutgoing.value,
    !isMessageDeleted(lastMsg),
  ].every(_ => _)
})

const startedTimestamp = computed(() => startedCallsTimestamps.value.get(room.value.pServedUser))

const pickupNotification = computed(() => Array.from(notifications.value.values()).find(({ type, eventData }) => (type === 'pickup' && eventData.target === room.value.number) || (type === 'self-pickup' && eventData.from === room.value.number)))

const { subscribe, unsubscribe } = useStatusSubscribe()

const onVisible = () => {
  subscribe(props.room.pServedUser)
}

const onInvisible = () => {
  unsubscribe(props.room.pServedUser)
}

onMounted(() => {
  if (props.isVisible) {
    onVisible()
  }
  if (startedTimestamp.value) {
    stop()
    start(getConferenceTimerOffsetSeconds(startedTimestamp.value))
  }
})

onBeforeUnmount(() => {
  onInvisible()
})

watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    onVisible()
  } else {
    onInvisible()
  }
})

watch(() => props.room.pServedUser, () => {
  if (props.isVisible) {
    onVisible()
  } else {
    onInvisible()
  }
})

const { start, stop, duration } = useSessionTimer()
watch(startedTimestamp, (n, o) => {
  if (n && n !== o) {
    stop()
    start(getConferenceTimerOffsetSeconds(n))
  } else {
    stop()
  }
})
</script>
